import { useState, useEffect, useMemo } from 'react'
import { useService } from '@evertel/di'
import { DepartmentController } from '@evertel/department'
import { IntegrationStore } from '@evertel/stores'
import { getRole, DepartmentAccess, getPrimaryDepartmentsAccess } from '@evertel/utils'

interface UseUserTaglineParams {
    departmentsAccess: DepartmentAccess[]
    /**
     * The department context to evaluate against.
     * - In a room: pass room.departmentId
     * - In a general list: pass selectedDepartmentId
     * - For profile/threads with no specific context: omit or pass undefined
     */
    contextDepartmentId?: number

    /**
     * The user's reporting department object.
     * Used when the user is a guest-only member (no non-guest memberships) to display
     * their reporting agency in the tagline instead of leaving the department blank.
     */
    reportingDepartment?: { position: string, department: string }

    /** The user's ID — required for connector bot resolution when isBot is true */
    userId?: number
    /** When true, skips department logic and returns the appropriate bot tagline */
    isBot?: boolean
}

interface UseUserTaglineOptions {
    /**
     * Fallback text when no position is available.
     * Defaults to 'No position'. Pass '' for empty string.
     */
    fallback?: string
    /**
     * Always show "@ DepartmentName" suffix, even if user is not a guest.
     * Useful for cross-agency searches where you always want to show origin.
     */
    forceShowDepartment?: boolean
    /**
     * Use department shortName when available, falling back to full name.
     * Defaults to true. Set to false to always use full department name.
     */
    shortDeptName?: boolean
}

interface UseUserTaglineResult {
    /** The formatted display string (e.g. "Detective @ Springfield PD") */
    displayPosition: string
    /** The position part only (e.g. "Detective"), or fallback text */
    positionText: string
    /** The department name part only (e.g. "Springfield PD"), or empty string */
    departmentName: string
    /** Whether the user is a guest in the context department */
    isGuest: boolean
    /** Whether the tagline is derived from the user's self-reported reporting department */
    isReportingDepartment: boolean
}

export const NO_POSITION_TEXT = 'No position'

/**
 * Hook to compute and format a user's position tagline for display.
 * Determines which department's position to show based on context,
 * fetches department name if needed, and formats the final string.
 *
 * @example
 * // In a room context
 * const { displayPosition, isGuest } = useUserTagline({
 *     departmentsAccess: user.departmentsAccess,
 *     contextDepartmentId: room.departmentId
 * })
 *
 * @example
 * // With empty fallback (show nothing if no position)
 * const { displayPosition } = useUserTagline(
 *     { departmentsAccess, contextDepartmentId },
 *     { fallback: '' }
 * )
 */
export function useUserTagline(
    params: UseUserTaglineParams,
    options: UseUserTaglineOptions = {}
): UseUserTaglineResult {
    const { departmentsAccess, contextDepartmentId, reportingDepartment, userId, isBot } = params
    const { fallback = NO_POSITION_TEXT, forceShowDepartment = false, shortDeptName = true } = options

    const departmentController = useService(DepartmentController, [])
    const integrationStore = useService(IntegrationStore, [])
    const [displayPosition, setDisplayPosition] = useState(fallback)
    const [positionText, setPositionText] = useState(fallback)
    const [departmentName, setDepartmentName] = useState('')

    // Compute which department to use and whether user is guest
    const tagline = useMemo(() => {
        // Bot users skip all department logic
        if (isBot) {
            const isConnectorBot = userId
                ? integrationStore.objectsArray.some(i => i.integrationUserId === userId)
                : false
            const botTagline = isConnectorBot ? 'Connector Bot' : 'Evertel Bot'
            return { position: botTagline, departmentId: null, showDepartmentName: false, isGuest: false }
        }

        if (!departmentsAccess?.length) {
            return { position: '', departmentId: null, reportingDepartmentName: undefined, showDepartmentName: false, isGuest: false }
        }

        // If we have a context department, evaluate user's relationship to it
        if (contextDepartmentId) {
            const contextAccess = departmentsAccess.find(
                da => da.departmentId === contextDepartmentId
            )
            const isGuestInContext = contextAccess
                ? getRole(contextAccess.roles) === 'guest'
                : false

            // User is a non-guest member of the context department
            if (contextAccess && !isGuestInContext) {
                return {
                    position: contextAccess.positionString?.trim() || NO_POSITION_TEXT,
                    departmentId: contextAccess.departmentId,
                    showDepartmentName: true,
                    isGuest: false
                }
            }

            // User is a guest in context (or not a member at all)
            // Show their primary non-guest department (if any)
            const nonGuestAccess = departmentsAccess.filter(
                da => getRole(da.roles) !== 'guest' && da.isVerified
            )
            const nonGuestPrimaryDept = getPrimaryDepartmentsAccess(nonGuestAccess)
            if (nonGuestPrimaryDept) {
                return {
                    position: nonGuestPrimaryDept.positionString?.trim() || NO_POSITION_TEXT,
                    departmentId: nonGuestPrimaryDept.departmentId,
                    showDepartmentName: true,
                    isGuest: true
                }
            }

            // User is a guest in context, no other memberships
            // If they have a reporting department set, use it for the tagline
            if (contextAccess) {
                return {
                    position: reportingDepartment?.position || contextAccess.positionString?.trim() || NO_POSITION_TEXT,
                    departmentId: null,
                    reportingDepartmentName: reportingDepartment?.department,
                    showDepartmentName: true,
                    isGuest: true
                }
            }
        }

        // No context department — check if user is guest-only with a reporting department
        const hasNonGuestMembership = departmentsAccess.some(
            da => getRole(da.roles) !== 'guest' && da.isVerified
        )

        if (!hasNonGuestMembership && reportingDepartment) {
            const first = departmentsAccess[0]
            return {
                position: reportingDepartment.position || first?.positionString?.trim() || NO_POSITION_TEXT,
                departmentId: null,
                reportingDepartmentName: reportingDepartment.department,
                showDepartmentName: true,
                isGuest: true
            }
        }

        // Use primary department
        const primaryDept = getPrimaryDepartmentsAccess(departmentsAccess)
        if (primaryDept) {
            return {
                position: primaryDept.positionString?.trim() || NO_POSITION_TEXT,
                departmentId: primaryDept.departmentId,
                showDepartmentName: true,
                isGuest: getRole(primaryDept.roles) === 'guest'
            }
        }

        // Final fallback
        const first = departmentsAccess[0]
        return {
            position: first?.positionString?.trim() || NO_POSITION_TEXT,
            departmentId: first?.departmentId ?? null,
            showDepartmentName: true,
            isGuest: getRole(first?.roles) === 'guest'
        }
    }, [departmentsAccess, contextDepartmentId, reportingDepartment, isBot, userId, integrationStore.objectsArray])

    // Fetch department name and format display string
    useEffect(() => {
        const { position, departmentId, reportingDepartmentName, showDepartmentName } = tagline

        // Reporting department name is already available, no fetch needed
        if (reportingDepartmentName) {
            setDisplayPosition(`${position} @ ${reportingDepartmentName}`)
            setPositionText(position)
            setDepartmentName(reportingDepartmentName)
            return
        }

        // Check if we should show department (either from logic or forced)
        const shouldShowDepartment = showDepartmentName || forceShowDepartment

        // Don't need to show department name, or no department to fetch
        if (!shouldShowDepartment || !departmentId) {
            setDisplayPosition(position || fallback)
            setPositionText(position || fallback)
            setDepartmentName('')
            return
        }

        // Fetch department name
        departmentController.init(departmentId).then(() => {
            const deptName = shortDeptName
                ? (departmentController.shortName || departmentController.department?.name || '')
                : (departmentController.department?.name || '')

            if (!position && !deptName) {
                setDisplayPosition(fallback)
                setPositionText(fallback)
                setDepartmentName('')
            } else if (!position) {
                setDisplayPosition(deptName ? `@ ${deptName}` : fallback)
                setPositionText(fallback)
                setDepartmentName(deptName)
            } else if (!deptName) {
                setDisplayPosition(position)
                setPositionText(position)
                setDepartmentName('')
            } else {
                setDisplayPosition(`${position} @ ${deptName}`)
                setPositionText(position)
                setDepartmentName(deptName)
            }
        })
    }, [tagline, departmentController, fallback, forceShowDepartment, shortDeptName])

    return {
        displayPosition,
        positionText,
        departmentName,
        isGuest: tagline.isGuest,
        isReportingDepartment: !!tagline.reportingDepartmentName
    }
}
